import { useState } from 'react';
import {
  getPickupErrorMessage,
  type FedExPickupPrefill,
  type PickupAddress,
  type PickupCarrierType,
  type PickupPackageLocation,
  type PickupWeightUnit,
} from '@/apis/fedexPickup';
import { useSchedulePickup } from '@/hooks/useFedExPickup';
import {
  convertWeight,
  isPickupFormComplete,
  roundWeight,
} from './fedexPickupUtils';
import { usePickupSchedule } from './usePickupSchedule';

export const usePickupForm = (
  shipmentId: string,
  prefill: FedExPickupPrefill,
  onScheduled?: (confirmationCode: string) => void
) => {
  const { schedule, isScheduling } = useSchedulePickup(shipmentId);

  const [carrierType, setCarrierType] = useState<PickupCarrierType>(
    prefill.carrierType
  );
  const [accountNumber, setAccountNumber] = useState(
    prefill.accountNumber ?? ''
  );
  const [address, setAddress] = useState<PickupAddress>({
    ...prefill.address,
    country: 'US',
  });
  const [contact, setContact] = useState(prefill.contact);
  const [packageCount, setPackageCount] = useState<number | null>(
    prefill.packageCount || 1
  );
  const [weight, setWeight] = useState<number | null>(prefill.weight);
  const [weightUnit, setWeightUnit] = useState<PickupWeightUnit>(
    prefill.weightUnit
  );
  const [packageLocation, setPackageLocation] =
    useState<PickupPackageLocation>('FRONT');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  const timing = usePickupSchedule({
    shipmentId,
    prefill,
    carrierType,
    accountNumber,
    address,
  });
  const { option, availabilityReady, readyTime, closeTime } = timing;

  // An ambiguous 409/504 can still leave a pickup scheduled; the refetched prefill then
  // carries it, and we show it instead of letting the operator submit again.
  const activePickup =
    prefill.activePickup?.status === 'SCHEDULED' ? prefill.activePickup : null;
  const scheduledCode = confirmationCode ?? activePickup?.confirmationCode;

  const handleCarrierChange = (next: PickupCarrierType) => {
    setCarrierType(next);
    timing.onCarrierChange(next);
  };

  const handleUnitChange = (next: PickupWeightUnit) => {
    setWeight((cur) =>
      cur == null ? cur : convertWeight(cur, weightUnit, next)
    );
    setWeightUnit(next);
  };

  const patchAddress = (patch: Partial<PickupAddress>) =>
    setAddress((cur) => ({ ...cur, ...patch }));

  const expressResidential = carrierType === 'EXPRESS' && address.residential;
  const residentialBlocked =
    address.residential &&
    (carrierType === 'EXPRESS' || option?.residentialAvailable === false);

  const valid =
    availabilityReady &&
    !isScheduling &&
    !residentialBlocked &&
    isPickupFormComplete({
      address,
      contact,
      readyTime,
      closeTime,
      packageCount,
      weight,
    });

  const handleSubmit = async () => {
    if (!valid || !packageCount || !option) return;
    setError(null);
    try {
      const pickup = await schedule({
        shipmentId,
        accountNumber,
        carrierType,
        pickupDate: option.pickupDate,
        readyTime,
        closeTime,
        address,
        contact,
        packageCount,
        weight: weight ? roundWeight(weight) : null,
        weightUnit,
        packageLocation,
        remarks,
      });
      setConfirmationCode(pickup.confirmationCode);
      onScheduled?.(pickup.confirmationCode);
    } catch (err) {
      setError(getPickupErrorMessage(err));
    }
  };

  return {
    timing,
    isScheduling,
    carrierType,
    handleCarrierChange,
    accountNumber,
    setAccountNumber,
    address,
    patchAddress,
    contact,
    setContact,
    packageCount,
    setPackageCount,
    weight,
    setWeight,
    weightUnit,
    handleUnitChange,
    packageLocation,
    setPackageLocation,
    remarks,
    setRemarks,
    error,
    confirmationCode,
    scheduledCode,
    expressResidential,
    residentialBlocked,
    valid,
    handleSubmit,
  };
};
